import { useState, useEffect, useRef } from 'react';

const FREE_WORD_LIMIT = 1500;
const WORDS_USED_KEY = 'openclaw_words_used';
const TOOL_GENS_KEY = 'openclaw_tool_gens';
const FIRST_USAGE_KEY = 'openclaw_first_usage';

const TOOL_COLORS: Record<string, { from: string; to: string; light: string; text: string }> = {
  'Paper Writer':        { from: '#7c3aed', to: '#6d28d9', light: 'bg-violet-50', text: 'text-violet-700' },
  'Genius Math':         { from: '#059669', to: '#0d9488', light: 'bg-emerald-50', text: 'text-emerald-700' },
  'Digital AI Search':   { from: '#0891b2', to: '#0369a1', light: 'bg-cyan-50', text: 'text-cyan-700' },
  'Resume Builder':      { from: '#f97316', to: '#d97706', light: 'bg-orange-50', text: 'text-orange-700' },
  'Business Plan Writer':{ from: '#e11d48', to: '#db2777', light: 'bg-rose-50', text: 'text-rose-700' },
  'eBook Creator':       { from: '#4f46e5', to: '#7c3aed', light: 'bg-indigo-50', text: 'text-indigo-700' },
  'Presentation Creator':{ from: '#c026d3', to: '#db2777', light: 'bg-fuchsia-50', text: 'text-fuchsia-700' },
  'Website Builder':     { from: '#0f766e', to: '#059669', light: 'bg-teal-50', text: 'text-teal-700' },
  'AI Image Generator':  { from: '#d97706', to: '#f97316', light: 'bg-amber-50', text: 'text-amber-700' },
  'Master Architect':    { from: '#475569', to: '#374151', light: 'bg-slate-50', text: 'text-slate-700' },
  'AI Video':            { from: '#ef4444', to: '#e11d48', light: 'bg-red-50', text: 'text-red-700' },
};

const TOOL_ICONS: Record<string, string> = {
  'Paper Writer': 'ri-file-text-line',
  'Genius Math': 'ri-calculator-line',
  'Digital AI Search': 'ri-search-eye-line',
  'Resume Builder': 'ri-profile-line',
  'Business Plan Writer': 'ri-briefcase-line',
  'eBook Creator': 'ri-book-open-line',
  'Presentation Creator': 'ri-slideshow-line',
  'Website Builder': 'ri-global-line',
  'AI Image Generator': 'ri-image-line',
  'Master Architect': 'ri-building-2-line',
  'AI Video': 'ri-video-line',
};

interface UsageData {
  wordsUsed: number;
  toolGens: Record<string, number>;
  firstUsage: number | null;
}

function loadData(): UsageData {
  let wordsUsed = 0;
  let toolGens: Record<string, number> = {};
  let firstUsage: number | null = null;
  try {
    wordsUsed = parseInt(localStorage.getItem(WORDS_USED_KEY) || '0', 10) || 0;
  } catch { /* ignore */ }
  try {
    const raw = localStorage.getItem(TOOL_GENS_KEY);
    if (raw) toolGens = JSON.parse(raw);
  } catch { /* ignore */ }
  try {
    const raw = localStorage.getItem(FIRST_USAGE_KEY);
    if (raw) firstUsage = parseInt(raw, 10) || null;
  } catch { /* ignore */ }
  return { wordsUsed, toolGens, firstUsage };
}

interface StatCardProps {
  icon: string;
  label: string;
  value: string;
  sub: string;
  color: string;
  delay: number;
  visible: boolean;
}

function StatCard({ icon, label, value, sub, color, delay, visible }: StatCardProps) {
  return (
    <div
      className="bg-white rounded-2xl p-5 border border-gray-100 flex flex-col gap-3 transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-bold uppercase tracking-widest text-gray-400">{label}</span>
        <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${color}`}>
          <i className={`${icon} text-sm text-white`}></i>
        </div>
      </div>
      <div>
        <p className="text-3xl font-extrabold text-gray-900 leading-none">{value}</p>
        <p className="text-xs text-gray-400 font-medium mt-1">{sub}</p>
      </div>
    </div>
  );
}

interface ToolBarProps {
  tool: string;
  count: number;
  maxCount: number;
  delay: number;
  visible: boolean;
}

function ToolBar({ tool, count, maxCount, delay, visible }: ToolBarProps) {
  const [barWidth, setBarWidth] = useState(0);
  const pct = maxCount > 0 ? (count / maxCount) * 100 : 0;
  const colors = TOOL_COLORS[tool] ?? { from: '#7c3aed', to: '#6d28d9', light: 'bg-violet-50', text: 'text-violet-700' };
  const icon = TOOL_ICONS[tool] ?? 'ri-apps-line';

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => setBarWidth(pct), delay + 100);
      return () => clearTimeout(t);
    } else {
      setBarWidth(0);
    }
  }, [visible, pct, delay]);

  return (
    <div
      className="flex items-center gap-3 group"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateX(0)' : 'translateX(-16px)',
        transition: `opacity 0.5s ease ${delay}ms, transform 0.5s ease ${delay}ms`,
      }}
    >
      <div className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
        style={{ background: `linear-gradient(135deg, ${colors.from}, ${colors.to})` }}>
        <i className={`${icon} text-white text-xs`}></i>
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-bold text-gray-700 truncate">{tool}</span>
          <span className="text-xs font-extrabold text-gray-900 ml-2 flex-shrink-0">
            {count} gen{count !== 1 ? 's' : ''}
          </span>
        </div>
        <div className="w-full h-2 rounded-full bg-gray-100 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-1000 ease-out"
            style={{
              width: `${barWidth}%`,
              background: `linear-gradient(90deg, ${colors.from}, ${colors.to})`,
            }}
          />
        </div>
      </div>
    </div>
  );
}

interface Props {
  darkMode: boolean;
  wordsUsedExternal: number;
  toolGensExternal: Record<string, number>;
}

export default function UsageDashboard({ darkMode, wordsUsedExternal, toolGensExternal }: Props) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const [ringOffset, setRingOffset] = useState(0);

  const data = loadData();
  const wordsUsed = wordsUsedExternal || data.wordsUsed;
  const toolGens = Object.keys(toolGensExternal).length > 0 ? toolGensExternal : data.toolGens;
  const firstUsage = data.firstUsage;

  const usedPct = Math.min((wordsUsed / FREE_WORD_LIMIT) * 100, 100);
  const remaining = Math.max(0, FREE_WORD_LIMIT - wordsUsed);
  const totalGens = Object.values(toolGens).reduce((a, b) => a + b, 0);

  // Days calculation
  let daysUntilLimit: number | null = null;
  let wordsPerDay: number | null = null;
  if (firstUsage && wordsUsed > 0) {
    const daysElapsed = Math.max(1, (Date.now() - firstUsage) / (1000 * 60 * 60 * 24));
    wordsPerDay = wordsUsed / daysElapsed;
    if (wordsPerDay > 0 && remaining > 0) {
      daysUntilLimit = Math.ceil(remaining / wordsPerDay);
    } else if (remaining <= 0) {
      daysUntilLimit = 0;
    }
  }

  // Sort tools by generation count
  const sortedTools = Object.entries(toolGens)
    .filter(([, c]) => c > 0)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 6);
  const maxGenCount = sortedTools.length > 0 ? sortedTools[0][1] : 1;

  // Ring dimensions
  const RING_R = 60;
  const RING_C = 2 * Math.PI * RING_R;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.15 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (visible) {
      const t = setTimeout(() => {
        setRingOffset(RING_C * (1 - usedPct / 100));
      }, 300);
      return () => clearTimeout(t);
    } else {
      setRingOffset(RING_C);
    }
  }, [visible, usedPct, RING_C]);

  const ringColor = usedPct >= 100 ? '#f87171' : usedPct >= 70 ? '#fbbf24' : '#34d399';
  const ringLabel = usedPct >= 100 ? 'Limit reached' : usedPct >= 70 ? 'Almost there' : 'Looking good';

  // If no usage at all, don't render the section
  if (wordsUsed === 0 && totalGens === 0) return null;

  return (
    <section
      ref={ref}
      className="py-20"
      style={{ background: darkMode ? 'rgb(12,10,24)' : 'rgb(248,247,255)' }}
    >
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div
          className="text-center mb-12 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(20px)' }}
        >
          <span className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-4 ${darkMode ? 'bg-violet-900/60 text-violet-300' : 'bg-violet-50 text-violet-700'}`}>
            <i className="ri-bar-chart-2-line"></i>
            Your Usage Stats
          </span>
          <h2 className={`text-3xl font-extrabold mb-3 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            Track Your Free Allowance
          </h2>
          <p className={`text-base max-w-md mx-auto ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            See exactly how you&apos;re using your free plan and when you&apos;ll need to upgrade.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Left: Word Usage Ring ── */}
          <div
            className={`rounded-3xl p-7 flex flex-col items-center justify-center gap-5 border transition-all duration-700 ${darkMode ? 'bg-gray-900/80 border-gray-800' : 'bg-white border-gray-100'}`}
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '100ms' }}
          >
            <p className={`text-xs font-bold uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>Word Allowance</p>
            {/* Ring */}
            <div className="relative" style={{ width: 148, height: 148 }}>
              <svg width="148" height="148" className="-rotate-90">
                <circle cx="74" cy="74" r={RING_R} fill="none" stroke={darkMode ? 'rgba(255,255,255,0.08)' : '#f1f0f9'} strokeWidth="10" />
                <circle
                  cx="74" cy="74" r={RING_R}
                  fill="none"
                  stroke={ringColor}
                  strokeWidth="10"
                  strokeLinecap="round"
                  strokeDasharray={RING_C}
                  strokeDashoffset={ringOffset}
                  style={{ transition: 'stroke-dashoffset 1.2s cubic-bezier(0.34,1.56,0.64,1), stroke 0.5s ease' }}
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-3xl font-extrabold" style={{ color: ringColor }}>{Math.round(usedPct)}%</span>
                <span className={`text-xs font-bold mt-0.5 ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>{ringLabel}</span>
              </div>
            </div>
            {/* Numbers */}
            <div className="w-full grid grid-cols-2 gap-3">
              <div className={`rounded-xl p-3 text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <p className={`text-xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{wordsUsed.toLocaleString()}</p>
                <p className={`text-xs font-medium mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Used</p>
              </div>
              <div className={`rounded-xl p-3 text-center ${darkMode ? 'bg-gray-800' : 'bg-gray-50'}`}>
                <p className={`text-xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>{remaining.toLocaleString()}</p>
                <p className={`text-xs font-medium mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Remaining</p>
              </div>
            </div>
            {/* Progress bar */}
            <div className="w-full">
              <div className={`w-full h-2 rounded-full overflow-hidden ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                <div
                  className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{
                    width: visible ? `${usedPct}%` : '0%',
                    background: `linear-gradient(90deg, ${ringColor}, ${usedPct >= 100 ? '#ef4444' : usedPct >= 70 ? '#f97316' : '#10b981'})`,
                    transitionDelay: '400ms',
                  }}
                />
              </div>
              <div className="flex items-center justify-between mt-1.5">
                <span className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>0</span>
                <span className={`text-xs font-medium ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{FREE_WORD_LIMIT.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* ── Middle: Generations by Tool ── */}
          <div
            className={`rounded-3xl p-7 border flex flex-col gap-5 transition-all duration-700 ${darkMode ? 'bg-gray-900/80 border-gray-800' : 'bg-white border-gray-100'}`}
            style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '200ms' }}
          >
            <div className="flex items-center justify-between">
              <p className={`text-xs font-bold uppercase tracking-widest ${darkMode ? 'text-gray-400' : 'text-gray-400'}`}>Generations by Tool</p>
              {totalGens > 0 && (
                <span className={`text-xs font-extrabold px-2.5 py-1 rounded-full ${darkMode ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                  {totalGens} total
                </span>
              )}
            </div>
            {sortedTools.length > 0 ? (
              <div className="flex flex-col gap-4 flex-1">
                {sortedTools.map(([tool, count], i) => (
                  <ToolBar
                    key={tool}
                    tool={tool}
                    count={count}
                    maxCount={maxGenCount}
                    delay={250 + i * 80}
                    visible={visible}
                  />
                ))}
              </div>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center gap-3 py-8">
                <div className={`w-12 h-12 flex items-center justify-center rounded-2xl ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`}>
                  <i className={`ri-apps-2-line text-xl ${darkMode ? 'text-gray-600' : 'text-gray-400'}`}></i>
                </div>
                <p className={`text-sm font-medium text-center ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  Generate something to see your tool breakdown
                </p>
              </div>
            )}
          </div>

          {/* ── Right: Projection + Stats ── */}
          <div className="flex flex-col gap-4">
            {/* Days until limit */}
            <div
              className={`rounded-3xl p-6 border flex flex-col gap-3 transition-all duration-700 ${
                daysUntilLimit === 0
                  ? 'bg-gradient-to-br from-rose-500 to-pink-600 border-rose-400'
                  : daysUntilLimit !== null && daysUntilLimit <= 7
                  ? 'bg-gradient-to-br from-amber-400 to-orange-500 border-amber-300'
                  : darkMode
                  ? 'bg-gray-900/80 border-gray-800'
                  : 'bg-white border-gray-100'
              }`}
              style={{ opacity: visible ? 1 : 0, transform: visible ? 'translateY(0)' : 'translateY(24px)', transitionDelay: '300ms' }}
            >
              <div className="flex items-center justify-between">
                <p className={`text-xs font-bold uppercase tracking-widest ${
                  daysUntilLimit === 0 || (daysUntilLimit !== null && daysUntilLimit <= 7)
                    ? 'text-white/70'
                    : darkMode ? 'text-gray-400' : 'text-gray-400'
                }`}>Pace Projection</p>
                <div className={`w-8 h-8 flex items-center justify-center rounded-xl ${
                  daysUntilLimit === 0 || (daysUntilLimit !== null && daysUntilLimit <= 7)
                    ? 'bg-white/20'
                    : darkMode ? 'bg-gray-800' : 'bg-orange-50'
                }`}>
                  <i className={`ri-fire-line text-sm ${
                    daysUntilLimit === 0 || (daysUntilLimit !== null && daysUntilLimit <= 7)
                      ? 'text-white'
                      : 'text-orange-500'
                  }`}></i>
                </div>
              </div>
              {daysUntilLimit !== null ? (
                <>
                  <div>
                    <p className={`text-4xl font-extrabold leading-none ${
                      daysUntilLimit === 0 || (daysUntilLimit !== null && daysUntilLimit <= 7)
                        ? 'text-white'
                        : darkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {daysUntilLimit === 0 ? '0' : daysUntilLimit > 999 ? '999+' : daysUntilLimit}
                    </p>
                    <p className={`text-sm font-semibold mt-1 ${
                      daysUntilLimit === 0 || (daysUntilLimit !== null && daysUntilLimit <= 7)
                        ? 'text-white/80'
                        : darkMode ? 'text-gray-400' : 'text-gray-500'
                    }`}>
                      {daysUntilLimit === 0 ? 'Limit reached — upgrade now' : `day${daysUntilLimit !== 1 ? 's' : ''} until limit at this pace`}
                    </p>
                  </div>
                  {wordsPerDay !== null && (
                    <p className={`text-xs font-medium ${
                      daysUntilLimit === 0 || (daysUntilLimit !== null && daysUntilLimit <= 7)
                        ? 'text-white/60'
                        : darkMode ? 'text-gray-500' : 'text-gray-400'
                    }`}>
                      ~{Math.round(wordsPerDay).toLocaleString()} words/day average
                    </p>
                  )}
                </>
              ) : (
                <div className="flex flex-col gap-1">
                  <p className={`text-3xl font-extrabold ${darkMode ? 'text-white' : 'text-gray-900'}`}>—</p>
                  <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Use the generator to see your pace</p>
                </div>
              )}
            </div>

            {/* Mini stat cards */}
            <div className="grid grid-cols-2 gap-4">
              <StatCard
                icon="ri-time-line"
                label="Words / day"
                value={wordsPerDay ? `~${Math.round(wordsPerDay).toLocaleString()}` : '—'}
                sub="at current pace"
                color="bg-gradient-to-br from-violet-500 to-violet-700"
                delay={400}
                visible={visible}
              />
              <StatCard
                icon="ri-flashlight-line"
                label="Total gens"
                value={totalGens > 0 ? String(totalGens) : '0'}
                sub="across all tools"
                color="bg-gradient-to-br from-cyan-500 to-sky-600"
                delay={480}
                visible={visible}
              />
            </div>
          </div>
        </div>

        {/* Upgrade nudge */}
        {usedPct >= 50 && (
          <div
            className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 transition-all duration-700"
            style={{ opacity: visible ? 1 : 0, transitionDelay: '600ms' }}
          >
            <p className={`text-sm font-medium ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              {usedPct >= 100
                ? 'Your free allowance is full. Upgrade for unlimited generations.'
                : `You've used ${Math.round(usedPct)}% of your free plan.`}
            </p>
            <a
              href="#pricing"
              onClick={e => { e.preventDefault(); document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' }); }}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-bold hover:shadow-lg hover:scale-105 transition-all cursor-pointer whitespace-nowrap"
            >
              <i className="ri-vip-crown-line text-sm"></i>
              View Pro Plans
              <i className="ri-arrow-right-line text-xs"></i>
            </a>
          </div>
        )}
      </div>
    </section>
  );
}
